<template>
  <div>
    <product-list
      v-bind="{ caller, loading, items , selectedFacets, ...$attrs, numResults }"
      :filters="filters"
      :search="search"
      @reached-bottom="onReachedBottom"
      @toggle-filter-drawer="toggleFilterDrawer"
      @clear="clear"
    >
      <template v-if="sortingItems[selectedSort]" #right>
        <base-dropdown-select
          v-model="selectedSort"
          :label="`${(selectedSort < 0) ? 'Sort by': sortingItems[selectedSort]}`"
          :items="sortingItems"
        />
      </template>
      <template #filters="{filter, index}">
        <base-chip :key="index" :label="filter.name" @delete="onFilterDelete(filter)" />
      </template>
      <template #default="{ item }">
        <product-item
          v-bind="{
            ...item,
            selectable: selectable,
            brand: item['brand']? item['brand']['name'] : '',
          }"
          :editable="false"
          @select="select(item, $event)"
        />
      </template>
    </product-list>

    <catalog-product-detail-modal
      :show="detailProduct != null"
      :product="detailProduct"
      @closed="detailProduct = null"
    />
  </div>
</template>

<script>
import Listable from '@/components/mixins/listable';
import { EventBus } from '@/event-bus';
import ProductList from './CatalogProductList';
import ProductItem from './CatalogProductItem';

export default {
  name: 'CatalogProductListContainer',
  components: {
    ProductList,
    ProductItem,
  },
  mixins: [Listable],
  inheritAttrs: false,
  props: {
    selectable: {
      type: Boolean,
      default: true,
    },

    // LISTABLE PROPS
    source: {
      type: String,
      default: 'catalog',
    },
    mode: {
      type: String,
      default: 'loadMore',
    },
    pageSize: {
      type: Number,
      default: 24,
    },
    customReset: {
      type: String,
      default: 'customReset',
    },
    noMultiRequest: {
      type: Boolean,
      default: true,
    },
    caller: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      filterQueryParams: {},
      sortingQueryParams: {},
      filters: [],
      detailProduct: null,
      listSelectedProduct: [],
      onSelected: false,
    };
  },
  computed: {
    selectedFacetsName() {
      return this.$store.state.catalog.selectedFacetsName;
    },
    selectedFacets() {
      return this.$store.getters['catalog/selectedFacets'];
    },
    regions() {
      return this.$store.state.account.user.profile.institution?.regions ?? [];
    },
    selectedMainFacetValueId() {
      return this.$store.state.catalog.selectedMainFacetValueId ?? null;
    },
    mainFacetWithValues() {
      return this.$store.getters('catalog/mainFacetWithValues');
    },
    selectedRegion() {
      const regionIdentifier = this.$store.getters['catalog/regionIdentifier'];
      const currentRegionObject = this.regions.find((region) => region.identifier === regionIdentifier);
      return currentRegionObject;
    },
    items() {
      return this.$store.state.catalog.items;
    },
    searchTerm() {
      return this.$store.state.catalog.search;
    },
    sortingItems() {
      return this.$store.getters['catalog/sortingItems'];
    },
    selectedMainFacetValueObject() {
      return this.$store.getters['catalog/selectedMainFacetValueObject'];
    },
    selectedSort: {
      get() {
        return this.$store.getters['catalog/selectedSort'];
      },
      set(value) {
        this.$store.dispatch('catalog/selectSortingValue', value);
      },
    },
  },
  watch: {
    searchTerm(newVal) {
      this.search = newVal;
    },
    selectedMainFacetValueId(newVal) {
      this.filterQueryParams[this.selectedMainFacetValueObject.facet_name] = this.selectedMainFacetValueObject.id;
      this.queryParams = { ...this.sortingQueryParams, ...this.filterQueryParams, in_catalogue: true }; // <-- makes API call!
      if (this.selectedRegion) {
        this.queryParams.region_id = this.selectedRegion.pk;
      }
    },
    selectedFacets(newVal) {
      // e.g. Make API calls to /product/ automatically when any filters/facets
      // are changed by reacting to Vuex state 'catalog/selectedFacets' ^
      // and mutating 'this.queryParams' which triggers an API call via Listable magic.
      //
      // ('newVal' is from:  src/_store/catalog.module.js:54's catalog/selectedFacets
      //  and represents an array of FacetValue id's)
      //
      // eslint-disable-next-line no-param-reassign
      newVal = newVal || [];
      this.reset();
      this.filterQueryParams = {};
      this.filters = [];

      for (let i = 0; i < this.selectedFacetsName.length; i += 1) {
        const fval = this.selectedFacetsName[i];
        const facetValueId = fval.id;
        const facetName = fval.facet_name;
        let updated,
          // eslint-disable-next-line prefer-const
          currParamVal = this.filterQueryParams[facetName];
        if (currParamVal) {
          updated = [currParamVal, facetValueId].join(',');
        } else {
          updated = facetValueId;
        }
        this.filterQueryParams[facetName] = updated;
        this.filters.push({
          id: facetValueId,
          name: `${fval.name}`,
        });
      }

      this.filterQueryParams[this.selectedMainFacetValueObject.facet_name] = this.selectedMainFacetValueObject.id;

      this.queryParams = { ...this.sortingQueryParams, ...this.filterQueryParams, in_catalogue: true }; // <-- makes API call!
      if (this.selectedRegion) {
        this.queryParams.region_id = this.selectedRegion.pk;
      }
    },
    selectedSort(newVal) {
      this.reset();
      this.sortingQueryParams.sort = this.$store.state.catalog.sortingValues[newVal].pk;
      this.regionIdentifier = this.$store.state.catalog.regionIdentifier;
      this.queryParams = { ...this.sortingQueryParams, ...this.filterQueryParams, in_catalogue: true };

      if (this.selectedRegion) {
        this.queryParams.region_id = this.selectedRegion.pk;
      }
    },
    selectedRegion(regionObject) {
      if (this.selectedRegion) {
        this.queryParams = {
          ...this.sortingQueryParams,
          ...this.filterQueryParams,
          in_catalogue: true,
          region_id:
          this.selectedRegion.pk,
        };
      }
    },
  },
  mounted() {
    this.$store.dispatch('catalog/clear');
    this.reset();
    this.$store.dispatch('catalog/fetchSortingValues').then(() => {
      // add query params on mounted to sort products by first sort value
      if (this.$store.state.catalog.sortingValues
           && this.$store.state.catalog.sortingValues.length > 0) {
        this.sortingQueryParams.sort = this.$store.state.catalog.sortingValues[0].pk;
        this.$set(this.queryParams, 'sort', this.$store.state.catalog.sortingValues[0].pk);
      }
    });
    EventBus.$on('closeModalSelector', (val) => {
      this.onSelected = val;
    });
    EventBus.$emit('isSort', this.sortingItems.length > 0);
    this.$set(this.item, 'selected', this.onSelected);
  },
  methods: {
    removeArray(array, element) {
      return array.filter((e) => e !== element);
    },
    resetNumberItem() {
      // eslint-disable-next-line array-callback-return
      this.listSelectedProduct.map((selectedItem, selectedIndex) => {
        const number = selectedIndex + 1;
        // eslint-disable-next-line array-callback-return
        this.items.map((item_) => {
          item_.images.map((image_) => {
            if (image_.url === selectedItem) {
              this.$set(image_, 'number', number);
            }
            return '';
          });
        });
      });
      return '';
    },
    select(item, imageIndex) {
      const image = item.images[imageIndex];
      if (this.selectable) {
        if (image.selected) {
          // unselected
          this.$set(image, 'selected', !image.selected);
          const deleteSelected = this.removeArray(this.listSelectedProduct, image.url);
          this.listSelectedProduct = deleteSelected;
          this.resetNumberItem();
          EventBus.$emit('selectedItem', (this.listSelectedProduct.length));
        } else {
          // selected
          if (typeof image.selected === 'undefined') {
            this.$set(image, 'selected', false);
          }
          this.$set(image, 'selected', true);
          this.listSelectedProduct.push(image.url);
          this.resetNumberItem();
          EventBus.$emit('selectedItem', (this.listSelectedProduct.length));
        }
        if (item.images.length > 0) {
          const images = item.images.filter((image_) => image_.selected);
          if (images.length > 0) {
            this.$set(item, 'selected', true);
          } else {
            this.$set(item, 'selected', false);
          }
        }
      } else {
        this.detailProduct = item;
      }
    },
    onFilterDelete(filter) {
      this.$store.dispatch('catalog/toggleSelectedFilter', filter);

      if (this.selectedFacets.length > 0) {
        const obj = {};
        for (let item = 0; item < this.selectedFacets.length; item += 1) {
          const facetValueId = this.selectedFacets[item];
          const facetName = this.selectedFacetsName[item].facet_name;
          if (!(facetName in obj)) {
            obj[facetName] = [facetValueId];
          } else {
            obj[facetName].push(facetValueId);
          }
        }

        Object.entries(obj).forEach(([k, v]) => {
          try {
            obj[k] = v.join(',');
          } catch (e) {
            console.log(e);
          }
        });
        this.$store.dispatch('catalog/fetchFacets', { specialHandling: false, additionalParams: obj });
      } else {
        this.$store.dispatch('catalog/fetchFacets', {});
      }
    },
    clear() {
      this.$store.dispatch('catalog/clearFilters');
    },
    toggleFilterDrawer() {
      this.$store.dispatch('catalog/toggleFilterDrawer');
    },
    onReachedBottom() {
      if (this.nextPageExists && !this.loading) this.loadMore();
    },
  },
};
</script>
